/*
 * speed-test/engine.js — orchestrates the measurement lanes of /speed-test.
 *
 * Page code talks only to this object. It owns: creating lanes (see
 * lane-worker.js for why there are four and how each one reaches its own
 * connection), warming them, the phase timeline, and the reduction of raw
 * reports to numbers via SpeedTestStats. It owns no DOM besides one hidden
 * credentialless iframe.
 *
 * Plan by capability:
 *   4 lanes (Chromium: credentialless frames)
 *       download  lanes 0,1,2 · upload lanes 0,2 (XHR) + 1 (streaming fetch)
 *       latency   lane 3 — never carries a transfer
 *   2 lanes (Firefox / Safari: no credentialless frames)
 *       download/upload lanes 0,1 for the first 65 % of the phase, then lane 1
 *       stops and probes latency while lane 0 keeps the line loaded. Throughput
 *       is read from the part where both lanes ran. Slower to state, but the
 *       loaded pings still never share a connection with a transfer.
 *   single mode (the person asked for one connection, as Ookla offers)
 *       one transfer lane, latency on a separate lane.
 */
(function (root) {
    'use strict';

    var S = root.SpeedTestStats;

    function now() { return performance.timeOrigin + performance.now(); }
    function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

    function MeasureError(kind) {
        var e = new Error(kind);
        e.name = 'MeasureError';
        e.kind = kind;
        return e;
    }
    function abortError() {
        var e = new Error('stopped');
        e.name = 'AbortError';
        return e;
    }

    function create(cfg) {
        var lanes = {};
        var framesClosed = false;
        var listeners = [];
        var frame = null;
        var stopped = false;
        var info = { lanes: 0, credentialless: false, streamUpload: false, server: '', colo: '', proto: '', edge: '', upUrl: '' };

        function dispatch(msg) { listeners.slice().forEach(function (fn) { try { fn(msg); } catch (e) {} }); }
        function on(fn) { listeners.push(fn); return function () { listeners = listeners.filter(function (x) { return x !== fn; }); }; }

        function wrapLane(id, where, cred, port, worker) {
            var lane = { id: id, where: where, cred: cred, port: port, worker: worker, ready: false, streamUpload: false };
            lane.readyP = new Promise(function (resolve) {
                // Generous: the worker script has to arrive first, and on a slow
                // phone network that alone can take seconds.
                var to = setTimeout(function () { resolve(false); }, cfg.laneTimeout || 15000);
                port.onmessage = function (ev) {
                    var m = ev.data || {};
                    if (m.k === 'ready') { lane.ready = true; lane.streamUpload = !!m.streamUpload; clearTimeout(to); resolve(true); return; }
                    dispatch(m);
                };
            });
            lane.send = function (cmd) { try { port.postMessage(cmd); } catch (e) {} };
            lanes[id] = lane;
            return lane;
        }

        function pageLane(id, cred) {
            try {
                var w = new Worker(cfg.workerUrl);
                var ch = new MessageChannel();
                w.postMessage({ t: 'port', lane: id, cred: cred }, [ch.port2]);
                return wrapLane(id, 'page', cred, ch.port1, w);
            } catch (e) { return null; }
        }

        function frameLanes(ids) {
            if (!('credentialless' in document.createElement('iframe')) || !cfg.laneHostUrl) return Promise.resolve([]);
            return new Promise(function (resolve) {
                var f = document.createElement('iframe');
                f.credentialless = true;
                f.setAttribute('credentialless', '');
                f.setAttribute('aria-hidden', 'true');
                f.tabIndex = -1;
                f.hidden = true;
                var done = false;
                function finish(list) { if (done) return; done = true; removeEventListener('message', onMsg); resolve(list); }
                function onMsg(e) {
                    if (e.source !== f.contentWindow || e.origin !== location.origin) return;
                    var d = e.data || {};
                    if (d.t !== 'st-lane-host') return;
                    // prepare() already moved on without the frame: do not let a
                    // late frame add lanes to a run that has been planned.
                    if (framesClosed) { finish([]); return; }
                    var made = ids.map(function (spec) {
                        var ch = new MessageChannel();
                        f.contentWindow.postMessage({ t: 'st-lane', lane: spec.id, cred: spec.cred, worker: new URL(cfg.workerUrl, location.href).href }, location.origin, [ch.port2]);
                        return wrapLane(spec.id, 'frame', spec.cred, ch.port1, null);
                    });
                    finish(made);
                }
                addEventListener('message', onMsg);
                setTimeout(function () { finish([]); }, 15000);
                f.src = cfg.laneHostUrl;
                document.body.appendChild(f);
                frame = f;
            });
        }

        /* Builds lanes once per page. Returns the capability summary. */
        var prepared = null;
        function prepare() {
            if (prepared) return prepared;
            prepared = (async function () {
                if (typeof Worker === 'undefined' || typeof MessageChannel === 'undefined') throw MeasureError('unsupported');
                framesClosed = false;
                var l0 = pageLane(0, 'same-origin');
                var l1 = pageLane(1, 'omit');
                var frP = frameLanes([{ id: 2, cred: 'same-origin' }, { id: 3, cred: 'omit' }]);
                if (!l0 || !(await l0.readyP)) throw MeasureError('unsupported');
                // Lane 0 is up, so the script is cached: the others get a short
                // grace, and a lane that is still not ready is simply not used.
                var fr = await Promise.race([frP, sleep(7000).then(function () { return []; })]);
                framesClosed = true;
                var extra = [l1].concat(fr).filter(Boolean);
                var ok = await Promise.all(extra.map(function (l) { return Promise.race([l.readyP, sleep(7000).then(function () { return false; })]); }));
                extra.forEach(function (l, i) { if (!ok[i]) delete lanes[l.id]; });
                if (!lanes[0]) throw MeasureError('unsupported');
                info.credentialless = !!(lanes[2] && lanes[3]);
                if (!info.credentialless) { delete lanes[2]; delete lanes[3]; }
                info.lanes = Object.keys(lanes).length;
                info.streamUpload = !!(lanes[1] && lanes[1].streamUpload);
                return info;
            })();
            prepared.catch(function () { prepared = null; });
            return prepared;
        }

        /* Waits for one message matching `pred`, or null after `ms`. */
        function waitFor(pred, ms) {
            return new Promise(function (resolve) {
                var off = on(function (m) { if (pred(m)) { off(); clearTimeout(t); resolve(m); } });
                var t = setTimeout(function () { off(); resolve(null); }, ms);
            });
        }

        var seq = 0;
        function nextId() { return ++seq; }

        function stopLanes() {
            Object.keys(lanes).forEach(function (k) { lanes[k].send({ cmd: 'stop' }); });
        }

        /* Opens every connection and learns who answered. */
        async function warmAll() {
            var ids = Object.keys(lanes).map(function (k) {
                var id = nextId();
                lanes[k].send({ cmd: 'warm', url: cfg.endpoints.ping, id: id });
                return id;
            });
            var res = await Promise.all(ids.map(function (id) { return waitFor(function (m) { return m.k === 'warm' && m.id === id; }, 6000); }));
            var good = res.filter(function (r) { return r && r.ok; });
            if (!good.length) throw MeasureError('server_unreachable');
            var r0 = good[0];
            info.server = r0.server || '';
            info.colo = r0.colo || '';
            info.proto = r0.proto || '';
            return good.length;
        }

        async function resolveUpload() {
            if (info.upUrl) return info.upUrl;
            var urls = [cfg.endpoints.up].concat(cfg.endpoints.upAlt ? [cfg.endpoints.upAlt] : []);
            for (var i = 0; i < urls.length; i++) {
                try {
                    var ctl = new AbortController();
                    var to = setTimeout(function () { ctl.abort(); }, 5000);
                    var r = await fetch(urls[i], { method: 'POST', body: new Uint8Array(2048), cache: 'no-store', signal: ctl.signal, headers: { 'Content-Type': 'application/octet-stream' } });
                    clearTimeout(to);
                    if (r.ok) { info.upUrl = urls[i]; return urls[i]; }
                } catch (e) {}
            }
            return '';
        }

        /* On the intl origin the blob is served from Cloudflare's edge cache. A
           quiet edge may not hold it (measured: first request MISS, then HIT),
           and a MISS streams from the Netherlands origin — the test would then
           measure that haul. A one-byte range request makes the edge fetch and
           cache it before timing starts; the status is reported, not assumed. */
        async function warmEdge() {
            if (!info.colo) return '';
            var status = '';
            for (var i = 0; i < 3; i++) {
                try {
                    var r = await fetch(cfg.endpoints.blob, { cache: 'no-store', headers: { Range: 'bytes=0-0' } });
                    await r.arrayBuffer();
                    status = (r.headers.get('cf-cache-status') || '').toUpperCase();
                    if (status === 'HIT' || !status) break;
                } catch (e) { break; }
                await sleep(700);
            }
            info.edge = status;
            return status;
        }

        function laneList(ids) { return ids.filter(function (i) { return lanes[i]; }).map(function (i) { return lanes[i]; }); }

        function plan(mode) {
            if (mode === 'single') {
                var probe = lanes[3] ? 3 : (lanes[1] ? 1 : null);
                return { dl: [0], ul: [{ id: 0, mode: 'xhr' }], probe: probe, shared: probe == null };
            }
            if (info.credentialless) {
                var ul = [{ id: 0, mode: 'xhr' }, { id: 2, mode: 'xhr' }];
                if (info.streamUpload) ul.push({ id: 1, mode: 'stream' });
                // Lane 3 only probes, it never transfers (the plan in this file's
                // header). Lending it to the transfer for the first 65 % was
                // measured wrong on 2026-10-29: after its download is aborted the
                // connection keeps draining the bytes already in flight, and the
                // probes queue behind them. Prod, alternating runs, same minute:
                //   RU (RTT 360 ms)  shared: download-under-load probes in 1 of 3
                //                    runs (3 samples, 578 ms = +207 ms invented),
                //                    download 58–70 Mbps;
                //                    reserved: 3 of 3 (17 samples, +5 ms), 72–86 Mbps
                //                    — the whole post-ramp window is counted, not 65 %;
                //   intl (RTT 23 ms) shared 92–106 Mbps, reserved 92–99, both 4/4.
                // 'shared' stays available for a stand that wants to compare.
                if (cfg.strategy === 'shared') {
                    ul.push({ id: 3, mode: info.streamUpload ? 'stream' : 'xhr' });
                    return { dl: [0, 1, 2, 3], ul: ul, probe: 3, shared: true };
                }
                return { dl: [0, 1, 2], ul: ul, probe: 3, shared: false };
            }
            if (lanes[1]) {
                return { dl: [0, 1], ul: [{ id: 0, mode: 'xhr' }].concat(info.streamUpload ? [{ id: 1, mode: 'stream' }] : []), probe: 1, shared: true };
            }
            return { dl: [0], ul: [{ id: 0, mode: 'xhr' }], probe: null, shared: false };
        }

        async function latency(probeId, count, onRtt) {
            var lane = lanes[probeId != null ? probeId : 0];
            var id = nextId();
            var samples = [];
            var off = on(function (m) {
                if (m.k === 'rtt' && m.id === id) {
                    if (m.ms != null) samples.push(m.ms);
                    if (onRtt) onRtt(m.ms, samples);
                }
            });
            lane.send({ cmd: 'ping', url: cfg.endpoints.ping, count: count, gap: 70, id: id });
            await waitFor(function (m) { return m.k === 'done' && m.dir === 'ping' && m.id === id; }, count * 3500 + 2000);
            off();
            if (stopped) throw abortError();
            return samples;
        }

        /*
         * One transfer phase.
         *   dir      'dl' | 'ul'
         *   durMs    phase length
         *   rampMs   excluded from the result
         *   budget   optional byte budget (monitoring) — ends the phase early
         */
        async function phase(dir, p, durMs, rampMs, budget, onLive) {
            var start = now() + 40;
            var end = start + durMs;
            var splitAt = p.shared ? start + durMs * 0.65 : end;
            var events = [], loaded = [];
            var bytes = 0, doneEarly = false;
            var probeId = nextId();

            var off = on(function (m) {
                if (m.k === 'b' && m.dir === dir) {
                    // A late report from an earlier phase or run is not this line's traffic.
                    if (m.t < start - 150) return;
                    events.push({ t: m.t, n: m.n });
                    bytes += m.n;
                    if (budget && bytes >= budget && !doneEarly) { doneEarly = true; end = Math.min(end, now()); splitAt = Math.min(splitAt, end); stopLanes(); }
                } else if (m.k === 'rtt' && m.id === probeId && m.ms != null) {
                    loaded.push(m.ms);
                }
            });

            var transfers = dir === 'dl'
                ? laneList(p.dl).map(function (l) { return { lane: l, cmd: { cmd: 'dl', url: cfg.endpoints.blob, streams: 2 } }; })
                : p.ul.filter(function (u) { return lanes[u.id]; }).map(function (u) {
                    return { lane: lanes[u.id], cmd: { cmd: 'ul', url: info.upUrl, streams: u.mode === 'stream' ? 1 : 2, mode: u.mode, maxChunk: cfg.maxChunk } };
                });

            transfers.forEach(function (tr, i) {
                var until = (p.shared && tr.lane.id === p.probe) ? splitAt : end;
                setTimeout(function () {
                    if (stopped) return;
                    tr.cmd.until = until;
                    tr.cmd.id = nextId();
                    tr.lane.send(tr.cmd);
                }, i * 120);
            });

            // Latency under load: starts once the transfers have ramped. On a
            // shared plan the probe lane joins only after it stops transferring.
            var probeStart = p.shared ? splitAt + 250 : start + Math.min(1500, rampMs);
            var probeTimer = null;
            if (p.probe != null && lanes[p.probe]) {
                probeTimer = setTimeout(function () {
                    if (stopped || doneEarly) return;
                    lanes[p.probe].send({ cmd: 'ping', url: cfg.endpoints.ping, gap: 120, until: end, id: probeId, timeout: 2500, timeoutCounts: true });
                }, Math.max(0, probeStart - now()));
            }

            var liveTimer = setInterval(function () {
                if (!onLive) return;
                var t = now();
                onLive({ dir: dir, mbps: S.recentRate(events, t, 1000), progress: Math.min(1, (t - start) / (end - start)), elapsed: t - start });
            }, 100);

            while (!stopped && !doneEarly && now() < end) await sleep(50);
            clearInterval(liveTimer);
            if (probeTimer) clearTimeout(probeTimer);
            stopLanes();
            await sleep(180); // last 100 ms reports land
            off();
            if (stopped) throw abortError();

            var countEnd = p.shared ? splitAt : end;
            var within = events.filter(function (e) { return e.t <= countEnd + 120; });
            var sum = S.summarizeThroughput(within, start, countEnd, { rampMs: rampMs, slices: 20 });
            return { summary: sum, loaded: loaded, bytes: bytes, start: start, end: end, events: events };
        }

        function watchPressure() {
            var states = [];
            var obs = null;
            try {
                if (typeof PressureObserver === 'function') {
                    obs = new PressureObserver(function (records) { records.forEach(function (r) { states.push(r.state); }); });
                    obs.observe('cpu', { sampleInterval: 1000 }).catch(function () {});
                }
            } catch (e) { obs = null; }
            return {
                busy: function () {
                    if (!states.length) return null;
                    var hot = states.filter(function (s) { return s === 'serious' || s === 'critical'; }).length;
                    return hot / states.length >= 0.3;
                },
                stop: function () { try { if (obs) obs.disconnect(); } catch (e) {} },
            };
        }

        /*
         * Full run. opts: {mode, dlMs, ulMs, rampMs, pingCount, dlBudget, ulBudget,
         *                  onPhase(name, info), onLive(obj), onRtt(ms, samples)}
         */
        async function run(opts) {
            opts = opts || {};
            stopped = false;
            var t0 = now();
            var mode = opts.mode === 'single' ? 'single' : 'multi';
            var dlMs = opts.dlMs || 10000, ulMs = opts.ulMs || 10000, rampMs = opts.rampMs || 2000;
            var phaseCb = opts.onPhase || function () {};
            var pressure = watchPressure();
            try {
                phaseCb('detecting');
                await prepare();
                if (stopped) throw abortError();
                await warmAll();
                await Promise.all([warmEdge(), resolveUpload()]);
                if (stopped) throw abortError();
                var p = plan(mode);
                phaseCb('ping', { lanes: info.lanes, plan: p });

                var idleRaw = await latency(p.probe != null ? p.probe : 0, opts.pingCount || 14, opts.onRtt);
                var ping = S.summarizeLatency(idleRaw, 2);
                if (!ping) throw MeasureError('ping_failed');

                phaseCb('download', { plan: p });
                var dl = await phase('dl', p, dlMs, rampMs, opts.dlBudget, opts.onLive);
                if (!dl.summary || dl.bytes < 65536) throw MeasureError('download_failed');

                var ul = null;
                if (info.upUrl) {
                    phaseCb('upload', { plan: p });
                    ul = await phase('ul', p, ulMs, rampMs, opts.ulBudget, opts.onLive);
                    if (!ul.summary || ul.bytes < 65536) ul = null;
                }

                var loadedAll = dl.loaded.concat(ul ? ul.loaded : []);
                var bloatDl = S.bufferbloat(ping.median, dl.loaded);
                var bloatUl = ul ? S.bufferbloat(ping.median, ul.loaded) : null;
                // The worse direction decides; when neither phase alone has enough
                // probes for a verdict, the two together still describe the line.
                var bloat = [bloatDl, bloatUl].filter(Boolean).sort(function (a, b) { return b.increase - a.increase; })[0]
                    || S.bufferbloat(ping.median, loadedAll);

                return {
                    mode: mode,
                    plan: { dl: p.dl.length, ul: p.ul.length, probeSeparate: p.probe != null, shared: p.shared },
                    info: {
                        lanes: info.lanes, credentialless: info.credentialless, streamUpload: info.streamUpload,
                        server: info.server, colo: info.colo, proto: info.proto, edge: info.edge,
                    },
                    ping: ping,
                    download: dl.summary,
                    upload: ul ? ul.summary : null,
                    bloat: bloat,
                    bloatDl: bloatDl,
                    bloatUl: bloatUl,
                    loadedCount: loadedAll.length,
                    cpuBusy: pressure.busy(),
                    ms: Math.round(now() - t0),
                    series: { dl: dl.events, ul: ul ? ul.events : [], dlStart: dl.start, ulStart: ul ? ul.start : null },
                };
            } finally {
                pressure.stop();
                stopLanes();
            }
        }

        function stop() { stopped = true; stopLanes(); }

        function dispose() {
            stop();
            Object.keys(lanes).forEach(function (k) {
                try { lanes[k].port.close(); } catch (e) {}
                try { if (lanes[k].worker) lanes[k].worker.terminate(); } catch (e) {}
            });
            lanes = {};
            if (frame && frame.parentNode) frame.parentNode.removeChild(frame);
            frame = null;
            prepared = null;
        }

        return { prepare: prepare, run: run, stop: stop, dispose: dispose, info: function () { return info; } };
    }

    root.SpeedTestEngine = { create: create };
})(window);
